import "react-pdf/dist/esm/Page/TextLayer.css";

import NoImportBoard from "assets/img/svg/emptyState/noImportBoard";
import Upload from "assets/img/svg/upload";
import ModalBase from "components/shared/ModalBase";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import { useAppDispatch, useAppSelector } from "store/store";
import { transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import type { ITransferTerm } from "store/transferTermsStore";
import { fetchTransferTermAgreement } from "store/transferTermsStore";

import styles from "./style.module.scss";
import TransferTermAgreementModal from "./TransferTermAgreementModal";

interface IAgreementTab {
    transferTerm: ITransferTerm | undefined;
    onAgreementUpload: (term: ITransferTerm) => void;
}

/**
 * Tab in TransferTermDrawer. Either displays buttons to add agreement or displays agreement if one is saved.
 */
const AgreementTab = ({ transferTerm, onAgreementUpload }: IAgreementTab) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [modalType, setModalType] = useState("");
    const [numberPages, setNumberPages] = useState<number>();

    const { transferTermAgreement } = useAppSelector((state) => state.transferTerms);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumberPages(numPages);
    };

    const renderDocumentLoader = () => {
        return (
            <div className={styles["document-loader"]}>
                <Spinner />
            </div>
        );
    };

    // Fetches link to display PDF.
    useEffect(() => {
        if (transferTerm && transferTerm.agreementId) {
            dispatch(fetchTransferTermAgreement({ transferTermId: transferTerm?.id }));
        }
    }, []);

    return (
        <div className={styles["agreement-tab"]}>
            {!transferTerm?.agreementId ? (
                <div className={styles["agreement-upload"]}>
                    <div>
                        <h3>{t("TransferTerms.TransferTerm")}</h3>
                        <span>
                            {t("TransferTerms.Drawer.UploadAgreement", {
                                titleName: transferWorkLabel(transferTerm ?? {}),
                                transfereeName: transferTerm?.transfereeName,
                            })}
                        </span>
                    </div>
                    <StyledButton buttonType="outlined" onClick={() => setModalType("addAgreement")}>
                        <Upload />
                        {t("Articles.AddManually")}
                    </StyledButton>
                    <div
                        style={{ display: "flex", justifyContent: "center", marginTop: "1rem" } as React.CSSProperties}
                    >
                        <NoImportBoard />
                    </div>
                </div>
            ) : (
                transferTermAgreement && (
                    <Document
                        file={`data:application/pdf;base64,${transferTermAgreement}`}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={renderDocumentLoader()}
                    >
                        {Array.apply(null, Array(numberPages))
                            .map((x, i) => i + 1)
                            .map((page) => (
                                <Page
                                    renderAnnotationLayer={false}
                                    pageNumber={page}
                                    scale={1.5}
                                    key={`page${page}o${numberPages}`}
                                />
                            ))}
                    </Document>
                )
            )}
            <ModalBase
                modalTitle={t("TransferTerms.AgreementModal.AddSignedContract")}
                modalSubtitle={t("TransferTerms.AgreementModal.UploadPDF")}
                open={modalType === "addAgreement"}
                onClose={() => setModalType("")}
            >
                <TransferTermAgreementModal
                    transferTerm={transferTerm}
                    onEdit={onAgreementUpload}
                    onClose={() => setModalType("")}
                />
            </ModalBase>
        </div>
    );
};

export default AgreementTab;
